import React, { useState } from "react";
import {
  Mail,
  MapPin,
  User as UserIcon,
  Eye,
  EyeOff,
  Utensils,
  AlertTriangle,
  ArrowLeft,
  Camera,
} from "lucide-react";
import { User, Address, LoginResponse } from "../types";
import { useTranslation } from "react-i18next";
import GooglePlacesAutocomplete from "react-google-places-autocomplete";
import { register } from "../api/auth";
import { toast } from "sonner";
import "react-phone-number-input/style.css";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";

interface RegisterProps {
  onRegister: (user: LoginResponse) => void;
}

const Register: React.FC<RegisterProps> = ({ onRegister }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    dateOfBirth: "",
    preferences: "",
    allergies: "",
    address: {
      street: "",
      city: "",
      country: "",
      zipCode: "",
      latitude: 0,
      longitude: 0,
      fullAddress: "",
      province: "",
    } as Address,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [addressValue, setAddressValue] = useState(null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddressSelect = (value: any) => {
    setAddressValue(value);
    if (!value || !value.value || !value.value.place_id) {
      return;
    }
    const placeId = value.value.place_id;

    if (!window.google || !window.google.maps || !window.google.maps.places) {
      console.error("Google Maps JavaScript API script is not loaded.");
      toast.error("Could not connect to Google Maps service.");
      return;
    }

    const service = new window.google.maps.places.PlacesService(
      document.createElement("div")
    );
    service.getDetails(
      {
        placeId: placeId,
        fields: ["address_component", "geometry.location"],
      },
      (place, status) => {
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          place &&
          place.geometry &&
          place.geometry.location &&
          place.address_components
        ) {
          const latLng = place.geometry.location;
          const addressComponents = place.address_components;

          const streetNumber =
            addressComponents.find((c) => c.types.includes("street_number"))
              ?.long_name || "";
          const route =
            addressComponents.find((c) => c.types.includes("route"))
              ?.long_name || "";
          const street = `${streetNumber} ${route}`.trim();
          const city =
            addressComponents.find((c) => c.types.includes("locality"))
              ?.long_name || "";
          const country =
            addressComponents.find((c) => c.types.includes("country"))
              ?.long_name || "";
          const zipCode =
            addressComponents.find((c) => c.types.includes("postal_code"))
              ?.long_name || "";

          const province =
            addressComponents.find((c) =>
              c.types.includes("administrative_area_level_1")
            )?.long_name || "";

          setFormData((prev) => ({
            ...prev,
            address: {
              street,
              city,
              country,
              zipCode,
              latitude: latLng.lat(),
              longitude: latLng.lng(),
              fullAddress: value.label,
              province,
            },
          }));
        } else {
          console.error(`Error fetching place details. Status: ${status}`);
          toast.error(`Could not fetch address details. Status: ${status}`);
        }
      }
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!photoFile) {
      toast.error("Please select a profile photo.");
      return;
    }
    if (!isValidPhoneNumber(formData.phone)) {
      toast.error("Please enter a valid phone number.");
      return;
    }
    setIsLoading(true);

    const registrationData = {
      ...formData,
      allergies: formData.allergies
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
      preferences: formData.preferences
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
      photo: photoFile,
    };

    const result = await register(registrationData);

    setIsLoading(false);

    if (result.error) {
      console.error("Registration failed:", result.error);
      toast.error(`Registration failed: ${result.error.message}`);
    } else if (result.data) {
      console.log("Registration successful:", result.data);
      onRegister(result.data);
    }
  };

  const nextStep = () => {
    console.log(formData);
    if (
      formData.firstName &&
      formData.lastName &&
      formData.address.fullAddress
    ) {
      setStep(2);
    } else {
      toast.error("Please fill in First Name, Last Name, and Address.");
    }
  };

  const prevStep = () => {
    setStep(1);
  };

  return (
    <>
      <div className="mb-4">
        <div className="flex items-center">
          <div
            className={`w-1/2 text-center ${
              step === 1 ? "text-orange-500" : "text-gray-500"
            }`}
          >
            <div className="font-semibold">{t("common.step")} 1</div>
            <div className="text-xs">{t("auth.step.step1")}</div>
          </div>
          <div
            className={`w-1/2 text-center ${
              step === 2 ? "text-orange-500" : "text-gray-500"
            }`}
          >
            <div className="font-semibold">{t("common.step")} 2</div>
            <div className="text-xs">{t("auth.step.step2")}</div>
          </div>
        </div>
        <div className="mt-2 h-1.5 w-full bg-gray-200 rounded-full">
          <div
            className="h-1.5 bg-orange-500 rounded-full transition-all duration-300"
            style={{ width: step === 1 ? "50%" : "100%" }}
          />
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {step === 1 ? (
          <>
            <div className="flex justify-center pb-4">
              <div className="relative">
                <label htmlFor="photo-upload" className="cursor-pointer group">
                  <div className="w-28 h-28 rounded-full bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden transition-all duration-300 group-hover:border-orange-500 group-hover:bg-gray-50">
                    {photoPreview ? (
                      <img
                        src={photoPreview}
                        alt="Preview"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <UserIcon className="w-12 h-12 text-gray-400 transition-all duration-300 group-hover:text-orange-500" />
                    )}
                  </div>
                  <div className="absolute bottom-0 right-0 bg-orange-500 p-2 rounded-full text-white shadow-md transform group-hover:scale-110 transition-transform duration-300">
                    <Camera className="w-5 h-5" />
                  </div>
                </label>
                <input
                  id="photo-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handlePhotoChange}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("auth.firstName")}
                </label>
                <div className="relative">
                  <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white text-sm"
                    placeholder={t("auth.yourFirstName")}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("auth.lastName")}
                </label>
                <div className="relative">
                  <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white text-sm"
                    placeholder={t("auth.yourLastName")}
                  />
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("auth.dateOfBirth")}
              </label>
              <div className="relative">
                <input
                  type="date"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white text-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("auth.address")}
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 z-10" />
                <GooglePlacesAutocomplete
                  apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
                  autocompletionRequest={{
                    componentRestrictions: {
                      country: ["ca"],
                    },
                    types: ["address"],
                  }}
                  selectProps={{
                    value: addressValue,
                    onChange: handleAddressSelect,
                    placeholder: t("auth.addressPlaceholder"),
                    styles: {
                      control: (base, state) => ({
                        ...base,
                        width: "100%",
                        paddingLeft: "2.5rem",
                        borderWidth: "1px",
                        borderColor: state.isFocused ? "#f97316" : "#e5e7eb",
                        borderRadius: "0.5rem",
                        backgroundColor: state.isFocused
                          ? "#ffffff"
                          : "#f9fafb",
                        boxShadow: state.isFocused
                          ? "0 0 0 2px #f97316"
                          : "none",
                        "&:hover": {
                          borderColor: state.isFocused ? "#f97316" : "#d1d5db",
                        },
                      }),
                      input: (base) => ({
                        ...base,
                        margin: "0",
                        padding: "0",
                        height: "2.5rem",
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isFocused
                          ? "#fed7aa"
                          : state.isSelected
                          ? "#fb923c"
                          : "#ffffff",
                        color: state.isSelected ? "#ffffff" : "#1f2937",
                        "&:hover": {
                          backgroundColor: "#fed7aa",
                        },
                      }),
                      menu: (base) => ({
                        ...base,
                        borderRadius: "0.5rem",
                        overflow: "hidden",
                      }),
                    },
                    classNamePrefix: "react-select",
                  }}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Preferences
              </label>
              <div className="relative">
                <Utensils className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <textarea
                  name="preferences"
                  value={formData.preferences}
                  onChange={handleInputChange}
                  rows={2}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white resize-none text-sm"
                  placeholder="e.g., spicy food, vegetarian (comma-separated)"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Allergies
              </label>
              <div className="relative">
                <AlertTriangle className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <textarea
                  name="allergies"
                  value={formData.allergies}
                  onChange={handleInputChange}
                  rows={2}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white resize-none text-sm"
                  placeholder="e.g., peanuts, shellfish (comma-separated)"
                />
              </div>
            </div>
            <button
              type="button"
              onClick={nextStep}
              className="w-full py-3 px-6 rounded-lg font-semibold transition-all duration-300 shadow-sm hover:shadow-md bg-orange-500 text-white hover:bg-orange-600"
            >
              {t("common.next")}
            </button>
          </>
        ) : (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("auth.emailAddress")}
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 transition-all duration-200 bg-gray-50 focus:bg-white text-sm"
                  placeholder={t("auth.yourEmail")}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("auth.phone")}
              </label>
              <div className="relative">
                <PhoneInput
                  international
                  defaultCountry="CA"
                  value={formData.phone}
                  onChange={(value) =>
                    setFormData((prev) => ({ ...prev, phone: value || "" }))
                  }
                  required
                  className="phone-input-custom"
                  placeholder={t("auth.phonePlaceholder")}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("auth.password")}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  className="w-full pl-4 pr-12 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 transition-all duration-200 bg-gray-50 focus:bg-white text-sm"
                  placeholder={t("auth.passwordPlaceholder")}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={prevStep}
                className="w-auto py-3 px-6 rounded-lg font-semibold transition-all duration-300 bg-gray-200 text-gray-700 hover:bg-gray-300"
              >
                {/* <ArrowLeft className="inline-block w-4 h-4 mr-2" /> */}
                {t("common.back")}
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className={`w-full py-3 px-6 rounded-lg font-semibold transition-all duration-300 shadow-sm hover:shadow-md ${
                  isLoading
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-orange-500 text-white hover:bg-orange-600"
                }`}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>{t("auth.connecting")}</span>
                  </div>
                ) : (
                  t("auth.createAccount")
                )}
              </button>
            </div>
          </>
        )}
      </form>
    </>
  );
};

export default Register;
